import { useCallback, useState } from 'react';

import { useDeviceType } from '@picsart/design-system/hooks/device';
import { useAppStore, type IAppStore } from '@/store';
import useAuth from '@/hooks/useAuth';
import useGenerate from '@/hooks/useGenerate';
import { useCredits } from '@/features/credits';
import sendGenerateClickAnalytics from '@/utils/analytics/sendGenerateClickAnalytics';
import openRegistrationModal from '@/utils/user/openRegistrationModal';
import { Screen } from '@/types';
import { RightPanelTab } from '@/store/navigation/types';

const selector = (store: IAppStore) => ({
  selectedPreset: store.selectedPreset,
  setScreen: store.setScreen,
  setRightPanelTab: store.setRightPanelTab,
});

/**
 * Owns everything the Generate button needs: auth gate → credit gate →
 * route to the right post-submit screen → fire the generation.
 *
 * The button doesn't see useGenerate / useCredits / useAuth — those are
 * implementation details composed here once.
 */
const useGenerateAction = () => {
  const { generate } = useGenerate();
  const { isAuthorized } = useAuth();
  const { requireCredits } = useCredits();
  const { isMobile } = useDeviceType();
  const { selectedPreset, setScreen, setRightPanelTab } = useAppStore(selector);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGenerate = useCallback(async () => {
    if (!selectedPreset?.flowId) return;
    sendGenerateClickAnalytics({
      category: selectedPreset.category ?? '',
      id: selectedPreset.slug ?? '',
    });
    setIsSubmitting(true);
    try {
      if (!isAuthorized) {
        openRegistrationModal();
        return;
      }
      const hasCredits = await requireCredits({
        flowId: selectedPreset.flowId,
        presetCreditCount: selectedPreset.presetCreditCount,
      });
      if (!hasCredits) return;

      // Tablet+ flips the right panel to creations; mobile navigates screens.
      if (isMobile) setScreen(Screen.Files);
      else setRightPanelTab(RightPanelTab.Creations);

      await generate();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    generate,
    requireCredits,
    selectedPreset?.flowId,
    selectedPreset?.presetCreditCount,
    selectedPreset?.category,
    selectedPreset?.slug,
    isAuthorized,
    isMobile,
    setScreen,
    setRightPanelTab,
  ]);

  return { isSubmitting, handleGenerate };
};

export default useGenerateAction;
